import React, { useState, useEffect, useMemo } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { generateHoraRemedySchedule } from '../../utils/v518/horaSchedulerEngine';
import { DayHoraSchedule, HoraPeriod } from '../../types/v518';
import { 
  Sun, 
  Moon, 
  Clock, 
  Calendar, 
  Sparkles, 
  CheckCircle2, 
  ShieldCheck, 
  ChevronRight,
  Flame,
  Info
} from 'lucide-react';

interface HoraRemedySchedulerProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const HoraRemedyScheduler: React.FC<HoraRemedySchedulerProps> = ({
  kundali,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const [timePrefVersion, setTimePrefVersion] = useState<number>(0);

  useEffect(() => {
    const handleTimePrefChange = () => {
      setTimePrefVersion((v) => v + 1);
    };
    window.addEventListener('astro_time_format_changed', handleTimePrefChange);
    return () => window.removeEventListener('astro_time_format_changed', handleTimePrefChange);
  }, []);

  const schedulerData = useMemo(() => {
    return generateHoraRemedySchedule(kundali, lang);
  }, [kundali, lang, timePrefVersion]);

  const [selectedDayIdx, setSelectedDayIdx] = useState<number>(0);
  const [filterPlanet, setFilterPlanet] = useState<string>('ALL');

  const selectedDay: DayHoraSchedule = schedulerData.sevenDaySchedule[selectedDayIdx] || schedulerData.todaySchedule;

  const currentHora = schedulerData.currentActiveHora;

  const filteredHoras = selectedDay.horas.filter(h => {
    if (filterPlanet === 'ALL') return true;
    return h.planet === filterPlanet;
  });

  return (
    <div id="v518-hora-remedy-scheduler" className="bg-white dark:bg-astro-primary border border-astro-border dark:border-astro-strong-border rounded-xl p-5 md:p-6 shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-astro-border dark:border-astro-strong-border">
        <div className="flex items-center gap-2.5">
          <span className="p-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <Sun className="w-5 h-5" />
          </span>
          <div>
            <h3 className="text-lg font-semibold text-astro-text dark:text-white flex items-center gap-2">
              {isHi ? 'ग्रह होरा चक्र एवं उपचारात्मक समय सारिणी' : 'Planetary Hora & Remedy Scheduler'}
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 font-normal">
                V5.16
              </span>
            </h3>
            <p className="text-xs text-astro-muted dark:text-astro-muted">
              {isHi 
                ? 'काल्डियन होरा क्रम, दिन-रात के 24 होरा कालखंड एवं शुभ अनुष्ठान मुहूर्त' 
                : 'Chaldean Planetary Horas, 24-hour daily segments & auspicious remedy alignments'}
            </p>
          </div>
        </div>

        {/* 7-Day Day Selector */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
          {schedulerData.sevenDaySchedule.map((day, idx) => (
            <button
              key={day.date}
              id={`day-tab-${day.date}`}
              onClick={() => setSelectedDayIdx(idx)}
              className={`px-3 py-1 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                selectedDayIdx === idx
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted hover:text-astro-text dark:hover:text-white'
              }`}
            >
              {idx === 0 ? (isHi ? 'आज' : 'Today') : isHi ? day.dayOfWeekHi.slice(0, 3) : day.dayOfWeek.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>

      {/* Live Active Hora Banner */}
      <div className="my-5 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-yellow-500/10 border border-amber-300/40 dark:border-amber-700/40 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <span className="flex h-3 w-3 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
            </span>
          </div>
          <div>
            <div className="text-xs text-amber-800 dark:text-amber-300 font-semibold uppercase tracking-wider">
              {isHi ? 'वर्तमान सक्रिय होरा' : 'Currently Active Hora'}
            </div>
            <div className="text-base font-bold text-astro-text dark:text-white flex items-center gap-2 mt-0.5">
              <span>{currentHora.planet} {isHi ? `(${currentHora.planetHi})` : ''}</span>
              <span className="text-xs font-normal text-astro-muted dark:text-astro-muted">
                ({currentHora.startTime} – {currentHora.endTime})
              </span>
            </div>
          </div>
        </div>

        <div className="text-xs text-astro-secondary-text dark:text-astro-muted max-w-md">
          <span className="font-semibold text-amber-700 dark:text-amber-300">
            {isHi ? 'शुभ कार्य: ' : 'Aligned Activity: '}
          </span>
          {isHi ? currentHora.recommendedActivityHi : currentHora.recommendedActivityEn}
        </div>
      </div>

      {/* Day Overview Info */}
      <div className="flex items-center justify-between text-xs text-astro-muted dark:text-astro-muted mb-3 px-1">
        <div>
          {isHi ? selectedDay.dayOfWeekHi : selectedDay.dayOfWeek} ({selectedDay.date}) • {isHi ? 'दिन स्वामी:' : 'Day Lord:'} {selectedDay.dayLord}
        </div>
        <div>
          {isHi ? 'सूर्योदय:' : 'Sunrise:'} {selectedDay.sunrise} | {isHi ? 'सूर्यास्त:' : 'Sunset:'} {selectedDay.sunset}
        </div>
      </div>

      {/* Horas Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 max-h-[380px] overflow-y-auto pr-1">
        {filteredHoras.map((hora) => (
          <div
            key={`${selectedDay.date}-${hora.horaIndex}`}
            id={`hora-card-${hora.horaIndex}`}
            className={`p-3 rounded-lg border text-xs transition-all flex flex-col justify-between ${
              hora.isCurrent
                ? 'bg-amber-50/70 dark:bg-amber-950/20 border-amber-400 dark:border-amber-600 shadow-sm'
                : 'bg-astro-secondary-surface/50 dark:bg-astro-secondary/40 border-astro-border/70 dark:border-astro-strong-border/60 hover:border-astro-border'
            }`}
          >
            <div>
              <div className="flex items-center justify-between font-semibold text-astro-text dark:text-white mb-1">
                <div className="flex items-center gap-1.5">
                  {hora.isDayHora ? (
                    <Sun className="w-3.5 h-3.5 text-amber-500" />
                  ) : (
                    <Moon className="w-3.5 h-3.5 text-indigo-400" />
                  )}
                  <span>{hora.planet} {isHi ? `(${hora.planetHi})` : ''}</span>
                </div>
                <span className="text-[11px] text-astro-muted font-normal">
                  {hora.startTime} – {hora.endTime}
                </span>
              </div>
              <p className="text-[11px] text-astro-secondary-text dark:text-astro-muted mt-1 line-clamp-2" title={isHi ? hora.governingThemeHi : hora.governingThemeEn}>
                {isHi ? hora.governingThemeHi : hora.governingThemeEn}
              </p>
            </div>

            <div className="mt-2 pt-2 border-t border-astro-border/50 dark:border-astro-strong-border/50 flex items-center justify-between text-[10px] text-astro-muted">
              <span>{hora.isDayHora ? (isHi ? 'दिन होरा' : 'Day Hora') : (isHi ? 'रात्रि होरा' : 'Night Hora')}</span>
              <span className="text-amber-600 dark:text-amber-400 font-medium">
                {hora.alignedRemedyCategories[0] || 'Vedic Chanting'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Aligned Remedies Action Cards */}
      <div className="mt-6 pt-5 border-t border-astro-border dark:border-astro-strong-border">
        <div className="text-xs font-semibold text-astro-text dark:text-astro-muted uppercase tracking-wider mb-3">
          {isHi ? 'ग्रह अनुसार अनुशंसित उपचारात्मक समय' : 'Aligned Planetary Remedy Timers'}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {schedulerData.alignedRemedies.slice(0, 4).map((rem) => (
            <div
              key={rem.remedyId}
              id={`remedy-schedule-card-${rem.remedyId}`}
              className="p-3.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary/40 border border-astro-border/80 dark:border-astro-strong-border/70 text-xs flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between font-semibold text-astro-text dark:text-white mb-1">
                  <span>{isHi ? rem.remedyNameHi : rem.remedyNameEn}</span>
                  <span className="text-[11px] text-amber-600 dark:text-amber-400 font-normal">
                    {rem.category}
                  </span>
                </div>
                <div className="text-[11px] text-astro-secondary-text dark:text-astro-muted">
                  {isHi ? 'श्रेष्ठ समय: ' : 'Optimal Timing: '}{isHi ? rem.bestTimeOfDayHi : rem.bestTimeOfDayEn}
                </div>
              </div>

              {rem.upcomingOptimalSlots.length > 0 && (
                <div className="mt-2 pt-2 border-t border-astro-border/60 dark:border-astro-strong-border/60 text-[10px] text-astro-muted flex items-center gap-1">
                  <Clock className="w-3 h-3 text-astro-muted" />
                  <span>
                    {isHi ? 'आगामी स्लॉट: ' : 'Upcoming slot: '}
                    {rem.upcomingOptimalSlots[0].date} ({rem.upcomingOptimalSlots[0].timeWindow})
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
